import {
  ChargerAttributes,
  ChargerErrorSeverity,
  createEmptyCharger,
} from '../models/charger-model';
import { ChargerHistoryAttributes } from '../models/charger-history-model';
import { setPeak } from '../components/common/utils/peak-off-peak-field';

// Stable IDs / serials so cross-references between chargers and history work
// across reloads. These are used both for demo-mode interception and for
// matching faults/lookups in the troubleshooting flow.
export const DEMO_USER_ID = 'demo-user-host';

export const DEMO_CHARGER_IDS = {
  driveway: 'demo-charger-driveway',
  garage: 'demo-charger-garage',
  curbside: 'demo-charger-curbside',
} as const;

export const DEMO_PARTNER_CHARGER_IDS = {
  downtown: 'demo-partner-downtown',
  airport: 'demo-partner-airport',
  retail: 'demo-partner-retail',
} as const;

/** Flat partner fee shown in Partner Portal / Insights demo data. */
export const DEMO_PARTNER_EARNING_PER_CHARGE = 1;

const PEAK_HEX = setPeak({
  Sunday: { start: 0, end: 0 },
  Monday: { start: 16, end: 21 },
  Tuesday: { start: 16, end: 21 },
  Wednesday: { start: 16, end: 21 },
  Thursday: { start: 16, end: 21 },
  Friday: { start: 16, end: 21 },
  Saturday: { start: 0, end: 0 },
});

const baseCharger = (
  overrides: Partial<ChargerAttributes>
): ChargerAttributes =>
  createEmptyCharger({
    registered_user: DEMO_USER_ID,
    charger_email: 'host@meanderev.demo',
    manufacturer_name: 'Emporia',
    charger_model: 'Emporia X1',
    charger_type: 'Level 2',
    plug_type: 'J1772',
    charging_capacity: 40,
    currency: 'USD',
    country: 'US',
    state: 'CA',
    city: 'Boulder',
    zip_code: '94103',
    is_enabled: true,
    can_schedule: true,
    connected: true,
    peak_selection: PEAK_HEX,
    billing_type: 'kwh',
    financials: {
      price_kwh_peak: 0.45,
      price_kwh_off_peak: 0.28,
    },
    price_hourly_peak: 0,
    price_hourly_off_peak: 0,
    ...overrides,
  });

const buildDemoChargers = (): ChargerAttributes[] => {
  const driveway = baseCharger({
    uuid: DEMO_CHARGER_IDS.driveway,
    name: 'Driveway Charger',
    unit_serial: 'DEMO-DRV-001',
    meander_serial: 'MNDR-DRV-001',
    description: 'Single bay, residential driveway',
    address: '123 Maple Street',
    full_address: '123 Maple Street, Boulder, CO 80302',
    street_number: '123',
    street_name: 'Maple Street',
    lat: 40.0150,
    lng: -105.2705,
    lifetime_kwh: 4823,
    total_uptime: 18240,
    ocpp_connector_status: 'Available',
    vehicle_connected: false,
    vehicle_charging: false,
  });

  const garage = baseCharger({
    uuid: DEMO_CHARGER_IDS.garage,
    name: 'Garage Charger',
    unit_serial: 'DEMO-GRG-002',
    meander_serial: 'MNDR-GRG-002',
    description: 'Inside attached garage',
    address: '456 Oak Avenue',
    full_address: '456 Oak Avenue, Boulder, CO 80302',
    street_number: '456',
    street_name: 'Oak Avenue',
    lat: 40.0182,
    lng: -105.2810,
    lifetime_kwh: 6510,
    total_uptime: 22400,
    ocpp_connector_status: 'Charging',
    vehicle_connected: true,
    vehicle_charging: true,
    isOccupied: 1,
    current_power_w: 7200,
    current_energy_kwh: 12.3,
  });

  // The featured demo: a faulted OCPP charger with rich error metadata so
  // ChargerStatusBadge + ChargerStatusDetailModal light up with realistic
  // copy. The troubleshooting flow is launched from the detail modal.
  const curbside = baseCharger({
    uuid: DEMO_CHARGER_IDS.curbside,
    name: 'Curbside Charger',
    unit_serial: 'DEMO-CRB-003',
    meander_serial: 'MNDR-CRB-003',
    description: 'Public curbside, west side of building',
    address: '789 Pine Plaza',
    full_address: '789 Pine Plaza, Boulder, CO 80302',
    street_number: '789',
    street_name: 'Pine Plaza',
    lat: 40.0205,
    lng: -105.2755,
    lifetime_kwh: 9210,
    total_uptime: 30120,
    ocpp_connector_status: 'Faulted',
    vehicle_connected: false,
    vehicle_charging: false,
    error_code: 'GroundFailure',
    error_severity: 'error' as ChargerErrorSeverity,
    error_description:
      'The charger detected a ground fault on the charging circuit and has shut down to protect the vehicle and electrical system.',
    error_user_message: 'Ground fault detected - charger disabled for safety',
    error_recommended_action:
      'Restart the charger, then check that the cable is fully seated at both the charger and the vehicle. If the fault persists, submit photos to support.',
    vendor_id: 'Emporia',
    vendor_error_code: 'GFCI-021',
  });

  return [driveway, garage, curbside];
};

// In-memory mutable list so a charger registered live during the demo
// is appended and shows up in subsequent My Chargers responses.
let _demoChargers: ChargerAttributes[] | null = null;

export const getDemoChargers = (): ChargerAttributes[] => {
  if (_demoChargers == null) {
    _demoChargers = buildDemoChargers();
  }
  return _demoChargers;
};

export const findDemoCharger = (id: string): ChargerAttributes | undefined =>
  getDemoChargers().find(c => c.uuid === id);

export const addDemoCharger = (charger: ChargerAttributes): void => {
  const list = getDemoChargers();
  list.push(charger);
};

export const resetDemoChargers = (): void => {
  _demoChargers = null;
};

// Attach current-month revenue from freshly generated history so My Chargers
// "Your Net Revenue" is non-zero in demo mode.
export const getDemoChargersWithMonthlyRevenue = (): ChargerAttributes[] => {
  const history = getDemoHistory();
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);

  return getDemoChargers().map(charger => {
    const id = charger.uuid ?? '';
    const monthRecords = history.filter(h => {
      if (h.chargerId !== id) return false;
      const d = new Date(h.charging_start_time ?? 0);
      return d >= monthStart;
    });
    const gross = monthRecords.reduce(
      (sum, h) => sum + (h.total_charge_price ?? 0),
      0
    );
    const rounded = Math.round(gross * 100) / 100;
    return {
      ...charger,
      grossRevenue: rounded,
      netRevenue: rounded,
    };
  });
};

// ──────────────────────────────────────────────────────────────────────
// Transaction / charging-history fixtures
// ──────────────────────────────────────────────────────────────────────

const DAY_MS = 24 * 60 * 60 * 1000;

// Deterministic pseudo-random so the same demo always shows the same graph
// shape. We don't need cryptographic quality.
const seeded = (seed: number) => {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
};

const peakWindow = (date: Date): { peakStartHour: number; peakEndHour: number } => {
  const dow = date.getDay();
  if (dow === 0 || dow === 6) {
    return { peakStartHour: 0, peakEndHour: 0 };
  }
  return { peakStartHour: 16, peakEndHour: 21 };
};

interface SessionShape {
  startHour: number;
  durationMinutes: number;
  kwh: number;
}

const sessionShapeForDay = (
  dayIndex: number,
  chargerVariant: number,
  rand: () => number
): SessionShape[] => {
  // 0-2 sessions per day per charger, with some days having none for variety.
  const roll = rand();
  let sessionCount = 1;
  if (roll < 0.2) sessionCount = 0;
  else if (roll < 0.5) sessionCount = 1;
  else if (roll < 0.85) sessionCount = 2;
  else sessionCount = 3;

  const sessions: SessionShape[] = [];
  for (let i = 0; i < sessionCount; i++) {
    const startHour =
      Math.floor(rand() * 22) + (chargerVariant % 2 === 0 ? 0 : 1);
    const durationMinutes = Math.floor(40 + rand() * 200);
    const kwh = Math.round((1.5 + rand() * 16) * 100) / 100;
    sessions.push({ startHour, durationMinutes, kwh });
  }
  return sessions;
};

const buildHistoryForCharger = (
  charger: ChargerAttributes,
  variantIndex: number,
  daysBack: number,
  startHistoryId: number
): ChargerHistoryAttributes[] => {
  const records: ChargerHistoryAttributes[] = [];
  const rand = seeded(0xc0ffee + variantIndex * 7919);
  const now = Date.now();
  let nextId = startHistoryId;

  for (let dayOffset = daysBack - 1; dayOffset >= 0; dayOffset--) {
    const dayStart = new Date(now - dayOffset * DAY_MS);
    dayStart.setHours(0, 0, 0, 0);
    const { peakStartHour, peakEndHour } = peakWindow(dayStart);

    const sessions = sessionShapeForDay(dayOffset, variantIndex, rand);
    for (const s of sessions) {
      const start = new Date(dayStart);
      start.setHours(s.startHour, Math.floor(rand() * 60), 0, 0);
      const end = new Date(start.getTime() + s.durationMinutes * 60 * 1000);

      const startHour = start.getHours();
      const endHour = end.getHours();
      const overlapsPeak =
        peakStartHour !== peakEndHour &&
        endHour > peakStartHour &&
        startHour < peakEndHour;

      let peakKwh = 0;
      let offPeakKwh = s.kwh;
      let peakSeconds = 0;
      let offPeakSeconds = s.durationMinutes * 60;
      if (overlapsPeak) {
        const ratio = Math.min(
          1,
          Math.max(
            0,
            (Math.min(endHour, peakEndHour) -
              Math.max(startHour, peakStartHour)) /
              Math.max(1, endHour - startHour)
          )
        );
        peakKwh = Math.round(s.kwh * ratio * 100) / 100;
        offPeakKwh = Math.round((s.kwh - peakKwh) * 100) / 100;
        peakSeconds = Math.round(s.durationMinutes * 60 * ratio);
        offPeakSeconds = s.durationMinutes * 60 - peakSeconds;
      }

      const priceBasisPeak = charger.financials.price_kwh_peak ?? 0.45;
      const priceBasisOffPeak = charger.financials.price_kwh_off_peak ?? 0.28;
      const totalPrice =
        peakKwh * priceBasisPeak + offPeakKwh * priceBasisOffPeak;

      const startIso = start.toISOString();
      const endIso = end.toISOString();
      records.push({
        chargerId: charger.uuid ?? '',
        charging_start_time: startIso,
        charging_end_time: endIso,
        historyId: nextId++,
        off_peak_charge_time: offPeakSeconds,
        off_peak_kwh: offPeakKwh,
        orderId: `demo-order-${variantIndex}-${nextId}`,
        peak_charge_time: peakSeconds,
        peak_kwh: peakKwh,
        peak_selection_basis: charger.peak_selection,
        billing_type: 'kwh',
        price_basis_off_peak: priceBasisOffPeak,
        price_basis_peak: priceBasisPeak,
        service_date: startIso.split('T')[0],
        status: 'COMPLETE',
        tblCharger: { updatedAt: endIso },
        charger: {
          name: charger.name,
          full_address: charger.full_address,
        },
        total_charge_price: Math.round(totalPrice * 100) / 100,
        total_charge_time: s.durationMinutes * 60,
        total_kwh: s.kwh,
        tz_offset: start.getTimezoneOffset(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userId: DEMO_USER_ID,
        updatedAt: end,
        formattedDate: `${(start.getMonth() + 1)
          .toString()
          .padStart(2, '0')}/${start
          .getDate()
          .toString()
          .padStart(2, '0')}/${start.getFullYear()}`,
        tax: 0,
      });
    }
  }
  return records;
};

export const getDemoHistory = (): ChargerHistoryAttributes[] => {
  const chargers = getDemoChargers();
  const days = 90;
  const all: ChargerHistoryAttributes[] = [];
  let nextId = 100000;
  chargers.forEach((charger, idx) => {
    // Only chargers that have ever produced revenue have history — the
    // curbside fault charger still has prior history to show it used to
    // earn money.
    const batch = buildHistoryForCharger(charger, idx, days, nextId);
    nextId += batch.length + 1;
    all.push(...batch);
  });
  all.sort((a, b) => {
    const ta = new Date(a.charging_start_time ?? 0).getTime();
    const tb = new Date(b.charging_start_time ?? 0).getTime();
    return tb - ta;
  });
  return all;
};

export const resetDemoHistory = (): void => {
  // no-op: history is rebuilt relative to Date.now() on each access
};

// ──────────────────────────────────────────────────────────────────────
// Partner portal fixtures (Meander Partner Chargers)
// ──────────────────────────────────────────────────────────────────────

const buildDemoPartnerChargers = (): ChargerAttributes[] => {
  const downtown = baseCharger({
    uuid: DEMO_PARTNER_CHARGER_IDS.downtown,
    name: 'Downtown Garage L2',
    unit_serial: 'DEMO-PTN-001',
    meander_serial: 'MNDR-PTN-001',
    description: 'Meander partner — downtown parking garage',
    address: '200 Pearl Street',
    full_address: '200 Pearl Street, Boulder, CO 80302',
    street_number: '200',
    street_name: 'Pearl Street',
    lat: 40.0175,
    lng: -105.279,
    lifetime_kwh: 12450,
    total_uptime: 35600,
    ocpp_connector_status: 'Available',
    permissions: ['meander'],
  });

  const airport = baseCharger({
    uuid: DEMO_PARTNER_CHARGER_IDS.airport,
    name: 'Airport Plaza Charger',
    unit_serial: 'DEMO-PTN-002',
    meander_serial: 'MNDR-PTN-002',
    description: 'Meander partner — airport plaza lot',
    address: '4800 Airport Road',
    full_address: '4800 Airport Road, Boulder, CO 80301',
    street_number: '4800',
    street_name: 'Airport Road',
    lat: 40.039,
    lng: -105.252,
    lifetime_kwh: 8920,
    total_uptime: 28100,
    ocpp_connector_status: 'Charging',
    vehicle_connected: true,
    vehicle_charging: true,
    isOccupied: 1,
    current_power_w: 6800,
    current_energy_kwh: 8.4,
    permissions: ['meander'],
  });

  const retail = baseCharger({
    uuid: DEMO_PARTNER_CHARGER_IDS.retail,
    name: 'Retail Center Hub',
    unit_serial: 'DEMO-PTN-003',
    meander_serial: 'MNDR-PTN-003',
    description: 'Meander partner — retail shopping center',
    address: '950 28th Street',
    full_address: '950 28th Street, Boulder, CO 80303',
    street_number: '950',
    street_name: '28th Street',
    lat: 40.012,
    lng: -105.258,
    lifetime_kwh: 6340,
    total_uptime: 19800,
    ocpp_connector_status: 'Available',
    permissions: ['meander'],
  });

  return [downtown, airport, retail];
};

let _demoPartnerChargers: ChargerAttributes[] | null = null;

export const getDemoPartnerChargers = (): ChargerAttributes[] => {
  if (_demoPartnerChargers == null) {
    _demoPartnerChargers = buildDemoPartnerChargers();
  }
  return _demoPartnerChargers;
};

const filterHistoryByRange = (
  history: ChargerHistoryAttributes[],
  startDate?: string,
  endDate?: string
): ChargerHistoryAttributes[] => {
  const start = startDate ? new Date(startDate) : new Date(Date.now() - 30 * DAY_MS);
  const end = endDate ? new Date(endDate) : new Date();
  return history.filter(h => {
    const raw = h.charging_end_time ?? h.charging_start_time;
    if (!raw) return false;
    const d = new Date(raw);
    return d >= start && d <= end;
  });
};

const buildPartnerOrdersFromHistory = (
  history: ChargerHistoryAttributes[]
): Array<{
  updatedAt: string;
  partner_transaction_fee_applied: number;
  partner_kwh_markup_fee_applied: number;
  partner_discount_applied: number;
}> =>
  history.map(h => ({
    updatedAt: h.charging_end_time ?? h.charging_start_time ?? new Date().toISOString(),
    partner_transaction_fee_applied: DEMO_PARTNER_EARNING_PER_CHARGE,
    partner_kwh_markup_fee_applied: 0,
    partner_discount_applied: 0,
  }));

const buildPartnerEarningsTrend = (
  allHistory: ChargerHistoryAttributes[]
): Array<{
  month: string;
  meanderPartnerEarnings: number;
  revenueSplitEarnings: number;
  totalEarnings: number;
  orderCount: number;
}> => {
  const byMonth = new Map<string, number>();
  allHistory.forEach(h => {
    const raw = h.charging_end_time ?? h.charging_start_time;
    if (!raw) return;
    const month = raw.substring(0, 7);
    byMonth.set(month, (byMonth.get(month) ?? 0) + 1);
  });

  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, orderCount]) => {
      const earnings = orderCount * DEMO_PARTNER_EARNING_PER_CHARGE;
      return {
        month,
        meanderPartnerEarnings: earnings,
        revenueSplitEarnings: 0,
        totalEarnings: earnings,
        orderCount,
      };
    });
};

export type DemoPartnerTransactionDetails = {
  earningsTrend: ReturnType<typeof buildPartnerEarningsTrend>;
  chargers: Array<{
    charger: ChargerAttributes;
    chargerId: string;
    permissions: string[];
    chargerHistory: ChargerHistoryAttributes[];
    partnerEarnings: { orderCount: number; totalEarningsApplied: number };
    orders: ReturnType<typeof buildPartnerOrdersFromHistory>;
    earnings: {
      meanderPartner: {
        currentPeriod: {
          transactionFees: number;
          kwhMarkupFees: number;
          discounts: number;
          total: number;
          orderCount: number;
        };
        lifetime: {
          transactionFees: number;
          kwhMarkupFees: number;
          discounts: number;
          total: number;
          orderCount: number;
        };
      };
      combined: {
        currentPeriodTotal: number;
        lifetimeTotal: number;
        currentPeriodOrderCount: number;
        lifetimeOrderCount: number;
      };
    };
    lifetimeKwh: number;
  }>;
};

export const getDemoPartnerTransactionDetails = (req?: {
  startDate?: string;
  endDate?: string;
}): DemoPartnerTransactionDetails => {
  const partnerChargers = getDemoPartnerChargers();
  const days = 90;
  let nextId = 200000;

  const fullHistoryByCharger = new Map<string, ChargerHistoryAttributes[]>();
  partnerChargers.forEach((charger, idx) => {
    const batch = buildHistoryForCharger(charger, idx + 10, days, nextId);
    nextId += batch.length + 1;
    fullHistoryByCharger.set(charger.uuid ?? '', batch);
  });

  const allFullHistory = Array.from(fullHistoryByCharger.values()).flat();
  const earningsTrend = buildPartnerEarningsTrend(allFullHistory);

  const chargers = partnerChargers.map(charger => {
    const id = charger.uuid ?? '';
    const fullHistory = fullHistoryByCharger.get(id) ?? [];
    const filtered = filterHistoryByRange(
      fullHistory,
      req?.startDate,
      req?.endDate
    );
    const orders = buildPartnerOrdersFromHistory(filtered);
    const currentEarnings = orders.length * DEMO_PARTNER_EARNING_PER_CHARGE;
    const lifetimeEarnings = fullHistory.length * DEMO_PARTNER_EARNING_PER_CHARGE;

    return {
      charger,
      chargerId: id,
      permissions: ['meander'],
      chargerHistory: filtered,
      partnerEarnings: {
        orderCount: orders.length,
        totalEarningsApplied: currentEarnings,
      },
      orders,
      earnings: {
        meanderPartner: {
          currentPeriod: {
            transactionFees: currentEarnings,
            kwhMarkupFees: 0,
            discounts: 0,
            total: currentEarnings,
            orderCount: orders.length,
          },
          lifetime: {
            transactionFees: lifetimeEarnings,
            kwhMarkupFees: 0,
            discounts: 0,
            total: lifetimeEarnings,
            orderCount: fullHistory.length,
          },
        },
        combined: {
          currentPeriodTotal: currentEarnings,
          lifetimeTotal: lifetimeEarnings,
          currentPeriodOrderCount: orders.length,
          lifetimeOrderCount: fullHistory.length,
        },
      },
      lifetimeKwh: charger.lifetime_kwh ?? 0,
    };
  });

  return {
    // Omit top-level `earnings` so Partner Insights uses the legacy orders
    // path and daily $1/charge activity shows on the charts.
    earningsTrend,
    chargers,
  };
};

export const getDemoPartnerChargerTransactionDetails = (
  chargerId: string,
  req?: { startDate?: string; endDate?: string }
) => {
  const all = getDemoPartnerTransactionDetails(req);
  const entry = all.chargers.find(c => c.chargerId === chargerId);
  if (!entry) return null;
  return {
    charger: entry.charger,
    permissions: entry.permissions,
    chargerHistory: entry.chargerHistory,
    partnerEarnings: entry.partnerEarnings,
    partnerPayoutHistory: [],
    partnerPayouts: [],
  };
};
